import { useTranslation } from '@gem/modules/i18n';

import {
  categoryValidation,
  spaceAliasCreateValidation,
  spaceAliasEditValidation,
  spaceNameValidation,
} from '../fields/fields';
import type { IFormFieldValidation, IValidateParams, ValidationResult } from '../hooks';
import { useAsyncValidation, useSyncValidation } from '../hooks';
import { ValidationErrorType } from '../validation.types';

export interface ISpaceAliasAsyncDeps {
  spaceAliasAsyncValidation: (params: IValidateParams) => Promise<string | null>;
}

export interface ISpaceValidations {
  spaceName: IFormFieldValidation;
  spaceCategory: IFormFieldValidation;
  spaceAliasCreate: IFormFieldValidation;
  spaceAliasEdit: IFormFieldValidation;
}

export const useSharedSpaceValidations = (deps: ISpaceAliasAsyncDeps): ISpaceValidations => {
  const { t } = useTranslation();

  const nameErrorToText = {
    [ValidationErrorType.Empty]: ' ',
    [ValidationErrorType.TooShort]: t('username_min_length_error'),
  };

  const spaceCategoryErrorToText = {
    [ValidationErrorType.Empty]: ' ',
  };

  const aliasErrorToText = {
    [ValidationErrorType.Empty]: ' ',
    [ValidationErrorType.TooShort]: t('username_min_length_error'),
    [ValidationErrorType.IncorrectSymbols]: t('channel_alias_allowed_symbols_error'),
    [ValidationErrorType.AlreadyExists]: t('channel_link_is_not_available'),
  };

  const nameValidator = useSyncValidation();
  const spaceCategoryValidator = useSyncValidation();
  const aliasValidator = useAsyncValidation();

  return {
    spaceName: {
      validate: (value, _formData, meta): ValidationResult => {
        return nameValidator({
          value,
          meta: meta!,
          validate: spaceNameValidation,
          errorToText: nameErrorToText,
        });
      },
    },
    spaceCategory: {
      validate: (value, _formData, meta): ValidationResult => {
        return spaceCategoryValidator({
          value,
          meta: meta!,
          validate: categoryValidation,
          errorToText: spaceCategoryErrorToText,
        });
      },
    },
    spaceAliasCreate: {
      validate: (value, formData, meta): ValidationResult => {
        return aliasValidator({
          value,
          values: formData,
          meta: meta!,
          validate: spaceAliasCreateValidation,
          asyncValidate: deps.spaceAliasAsyncValidation,
          errorToText: aliasErrorToText,
        });
      },
    },
    spaceAliasEdit: {
      validate: (value, formData, meta): ValidationResult => {
        return aliasValidator({
          value,
          values: formData,
          meta: meta!,
          validate: spaceAliasEditValidation,
          asyncValidate: deps.spaceAliasAsyncValidation,
          errorToText: aliasErrorToText,
        });
      },
    },
  };
};
